import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Grid2,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FormHeadingWithBtns from "../../Components/Common/FormHeadingWithBtns";
import { CustomInputLabel } from "../../Components/Creators/CustomInputLabel";
import { CustomDropDown } from "../../Components/Common/CustomDropdown";
import {
  CustomInput,
  CustomTextArea,
} from "../../Components/Common/CustomInput";
import Layout from "../../layouts/Layout";
import TagInput from "../../Components/Content/TagInput";
import FileUpload from "../../Components/Common/FileUpload";
import {
  CreateContent,
  UpdateContent,
} from "../../redux/actions/posts.dispatcher";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../Components/Common/CustomSnackbar";
import { categoryFilters } from "../../assets/data";
import { useSelector } from "react-redux";

const ContentForm = () => {
  const suggestedHashtags = [
    "muaithai",
    "mma",
    "boxing",
    "exercise",
    "fitness",
    "running",
    "sports",
    "burningcals",
    "burningfat",
  ];
  const { userData } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();

  const [editItem, setEditItem] = useState(location?.state?.editItem || null);

  const [uploadUrl, setUploadUrl] = useState(editItem?.video || "");

  const [title, setTitle] = useState(editItem?.title || "");
  const [category, setCategory] = useState(editItem?.category || "");
  const [description, setDescription] = useState(editItem?.description || "");
  const [hashTags, setHashTags] = useState(editItem?.tags || []);
  const [postVisiblity, setPostVisiblity] = useState(
    editItem?.visibility ?? (userData.creatorId ? "" : "freeToEveryone")
  );
  const [subscriptionPrice, setSubscriptionPrice] = useState(
    editItem?.price || null
  );

  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    message: "",
  });
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setErrorState({ error: false, message: "" });

    if (!title) {
      setErrorState({
        error: true,
        message: "Caption cannot be empty",
      });
      return;
    }

    if (!postVisiblity) {
      setErrorState({
        error: true,
        message: "You havent selected a visibility for your content.",
      });
      return;
    }

    if (postVisiblity === "payPerView" && !subscriptionPrice) {
      setErrorState({
        error: true,
        message: "Please fill all the required fields",
      });
      return;
    }

    if (uploadUrl === null) {
      setErrorState({ error: true, message: "Please upload a video" });
      return;
    }

    try {
      setLoading(true);

      const postData = {
        title: title,
        description: description,
        tags: hashTags,
        visibility: postVisiblity,
        price: subscriptionPrice,
        video: uploadUrl,
      };

      const response = await CreateContent({ postData });

      if (response) {
        setSuccess(true);
        clearAllFields();
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }
    } catch (error) {
      setErrorState({ error: true, message: error.message });
    } finally {
      setLoading(false);
    }
  };
  const handleUpdate = async () => {
    setErrorState({ error: false, message: "" });

    if (!title) {
      setErrorState({
        error: true,
        message: "Caption cannot be empty",
      });
      return;
    }

    if (!postVisiblity) {
      setErrorState({
        error: true,
        message: "You havent selected a visibility for your content.",
      });
      return;
    }

    if (postVisiblity === "payPerView" && !subscriptionPrice) {
      setErrorState({
        error: true,
        message: "Please fill all the required fields",
      });
      return;
    }

    if (uploadUrl === null) {
      setErrorState({ error: true, message: "Please upload a video" });
      return;
    }

    try {
      setLoading(true);

      const postData = {
        title: title,
        description: description,
        tags: hashTags,
        visibility: postVisiblity,
        price: subscriptionPrice,
        video: uploadUrl,
      };

      const response = await UpdateContent({ postData }, editItem.id);

      if (response) {
        setSuccess(true);
        clearAllFields();
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }
    } catch (error) {
      setErrorState({ error: true, message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const clearAllFields = () => {
    setTitle("");
    setDescription("");
    setHashTags([]);
    setPostVisiblity("");
    setSubscriptionPrice(null);
    setUploadUrl(null);
  };

  const mediumScreen = useMediaQuery("(max-width:960px)");

  const handleAddHashtag = (tag) => {
    if (!hashTags.includes(tag)) {
      setHashTags([...hashTags, tag]);
    }
  };

  return (
    <Layout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar */}
      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, message: "" })}
        errorMsg={errorState.message}
      />
      <ConfirmationSnackbar
        snackbarState={success}
        handleSnackbarClose={() => setSuccess(false)}
        text={"Content created successfully"}
      />

      {/* Header */}
      <FormHeadingWithBtns
        title="Create Content"
        btnText={editItem ? "Update" : "Create"}
        onClick={editItem ? handleUpdate : handleSubmit}
      />

      {/* Form */}
      <Grid2 container spacing={2} sx={{ mt: 3, pb: 12 }}>
        {!editItem && (
          <Grid2 item size={{ xs: 12, md: 2 }}>
            <CustomInputLabel text="Media" required />
            <FileUpload
              fileType="video"
              setUploadUrl={setUploadUrl}
              uploadUrl={uploadUrl}
              setLoading={setLoading}
            />
          </Grid2>
        )}
        <Grid2 item size={{ xs: 12, md: 10 }}>
          <Grid2 container spacing={2}>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="Title" required />
              <CustomInput
                value={title}
                setValue={setTitle}
                noIcon
                placeholder="e.g. Check out my latest Muay Thai training video!"
              />
            </Grid2>
          </Grid2>

          <Box>
            <CustomInputLabel text="Description" />
            <Box>
              <CustomTextArea
                noIcon
                value={description}
                setValue={(val) => {
                  const trimmedVal = val.trimStart(); // trim only leading spaces
                  if (trimmedVal.length <= 300) setDescription(trimmedVal);
                }}
                placeholder="e.g. Drawing Added: Circle around Kick Target"
              />
              <Box textAlign="right" mt={0} fontSize="0.8rem" color="gray">
                {description.trim().length}/300
              </Box>
            </Box>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="Tags/Hashtags" />

              <TagInput hashTags={hashTags} setHashTags={setHashTags} />
              <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                {suggestedHashtags.slice(0, 9).map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    clickable
                    variant="outlined"
                    onClick={() => handleAddHashtag(tag)}
                  />
                ))}
              </Box>
            </Grid2>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="Subscription" required />
              <CustomDropDown
                value={postVisiblity}
                setValue={setPostVisiblity}
                readOnly={!userData.creatorId}
                title="e.g. Free"
                label="e.g. Free"
                data={[
                  { value: "freeToEveryone", label: "Free to everyone" },

                  {
                    value: "payPerViewToEveryone",
                    label: "Pay per View for everyone",
                  },
                  {
                    value: "payPerView",
                    label: "Pay Per View only for subscriber",
                  },
                  { value: "paid", label: "Free - only for subscribers" },
                ]}
                noIcon
                noMt
                noMb
              />
            </Grid2>
          </Grid2>

          {(postVisiblity === "payPerView" ||
            postVisiblity === "payPerViewToEveryone") && (
            <Grid2 container spacing={2}>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="Content Price" required />
                <CustomInput
                  value={subscriptionPrice}
                  setValue={setSubscriptionPrice}
                  noIcon
                  type="number"
                  placeholder="e.g. 10$"
                />
              </Grid2>
            </Grid2>
          )}
        </Grid2>
      </Grid2>
    </Layout>
  );
};

export default ContentForm;
